import { backupSchema, hasPermission, type ClientServer, type ServerStats } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Archive,
  ChevronRight,
  FolderOpen,
  History,
  Loader2,
  Play,
  Terminal,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { addSample, type Sample } from '@/components/console/usage-charts';
import { CopyChip } from '@/components/copy-chip';
import { heroVersion } from '@/components/game-hero';
import { EmptyState, SectionHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { ServerStatusAlerts } from '@/components/server-alerts';
import { serverAddress } from '@/components/server-card';
import { ServerOverview } from '@/components/server-overview';
import { PowerStateBadge, ServerStatusBadge, StatusPill } from '@/components/status-badges';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest } from '@/lib/api';
import { describeEvent, timeAgo } from '@/lib/format';
import { errorMessage } from '@/lib/forms';
import {
  activityEventsQuery,
  backupListQuery,
  serverQuery,
  startupQuery,
} from '@/lib/server-queries';
import { useServerSocket, type Connection } from '@/lib/server-socket';

export const Route = createFileRoute('/_app/server/$server/')({ component: OverviewPage });

/**
 * The server's landing page: what needs attention, live resources, the facts people copy
 * (address, version, SFTP) and shortcuts. The console lives on its own tab (/console).
 */
function OverviewPage() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const suspended = server.status === 'suspended';
  const prevStats = useRef<ServerStats | null>(null);
  const [history, setHistory] = useState<Sample[]>([]);
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [state, setState] = useState<ServerStats['state'] | null>(null);
  const [connection, setConnection] = useState<{ c: Connection; error?: string }>({
    c: 'connecting',
  });
  const socket = useServerSocket(suspended ? null : id, {
    onReady: () => setConnection({ c: 'connected' }),
    onStatus: setState,
    onStats: (s) => {
      setStats(s);
      setState(s.state);
      if (s.state === 'offline') {
        prevStats.current = null;
        return;
      }
      setHistory((h) => addSample(h, prevStats.current, s, Date.now()));
      prevStats.current = s;
    },
    onConnection: (c, error) => setConnection({ c, error }),
  });
  // The socket may already be connected (shared with the layout).
  const liveState = state ?? socket?.state ?? null;
  const connected = connection.c === 'connected' || socket?.connection === 'connected';
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);

  return (
    <div className="grid gap-6">
      <ServerStatusAlerts
        server={server}
        connection={connected ? { c: 'connected' } : connection}
      />
      <ServerOverview
        server={server}
        state={liveState}
        stats={stats}
        history={history}
        connected={connected}
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="grid content-start gap-6">
          <ServerFacts server={server} state={liveState} stats={stats} />
          <QuickActions server={server} />
        </div>
        {server.profile.modules.includes('activity') && can('activity.read') && (
          <RecentActivity server={id} />
        )}
      </div>
    </div>
  );
}

function ServerFacts({
  server,
  state,
  stats,
}: {
  server: ClientServer;
  state: ServerStats['state'] | null;
  stats: ServerStats | null;
}) {
  const startup = useQuery({
    ...startupQuery(server.identifier),
    enabled: Boolean(
      server.profile.identity.versionFrom && hasPermission(server.permissions, 'startup.read'),
    ),
  });
  const version = heroVersion(server.profile.identity, startup.data?.variables);
  const players = state && state !== 'offline' ? stats?.players : undefined;
  return (
    <section aria-label="Server details">
      <SectionHeader title="Server" />
      <Card className="gap-0 py-1">
        <dl className="divide-y text-sm">
          <Fact label="Status">
            {server.status ? (
              <ServerStatusBadge status={server.status} />
            ) : state ? (
              <PowerStateBadge state={state} />
            ) : (
              <StatusPill tone="muted" pulse>
                Connecting
              </StatusPill>
            )}
          </Fact>
          <Fact label="Address">
            <CopyChip value={serverAddress(server)} label="server address" />
          </Fact>
          {server.egg.playerQuery && (
            <Fact label="Players">
              {players ? (
                <span className="tabular-nums">
                  {players.online} / {players.max} online
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {state === 'offline' ? 'Server offline' : '—'}
                </span>
              )}
            </Fact>
          )}
          {version && <Fact label={version.label}>{version.value}</Fact>}
          <Fact label="Game">{server.egg.name}</Fact>
          <Fact label="Location">
            {server.node.location.long ?? server.node.location.short} · {server.node.name}
          </Fact>
          {hasPermission(server.permissions, 'file.sftp') && (
            <Fact label="SFTP">
              <CopyChip
                value={`sftp://${server.sftp.username}@${server.sftp.host}:${server.sftp.port}`}
                label="SFTP address"
              />
            </Fact>
          )}
        </dl>
      </Card>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-4 px-4 py-2.5">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}

/** Shortcuts the caller may use: each is a real link, or the existing backup endpoint. */
function QuickActions({ server }: { server: ClientServer }) {
  const queryClient = useQueryClient();
  const id = server.identifier;
  const has = (m: ClientServer['profile']['modules'][number]) => server.profile.modules.includes(m);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const backup = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${id}/backups`, {
        method: 'POST',
        body: { ignored: '' },
        schema: backupSchema,
      }),
    onSuccess: async () => {
      toast.success('Backup started. It appears under Backups when it finishes.');
      await queryClient.invalidateQueries({ queryKey: backupListQuery(id).queryKey });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const links: { to: string; label: string; icon: LucideIcon; show: boolean }[] = [
    { to: '/server/$server/console', label: 'Console', icon: Terminal, show: has('console') },
    {
      to: '/server/$server/files',
      label: 'Files',
      icon: FolderOpen,
      show: has('files') && can('file.read'),
    },
    {
      to: '/server/$server/startup',
      label: 'Startup',
      icon: Play,
      show: has('startup') && can('startup.read'),
    },
  ];
  const canBackup = has('backups') && can('backup.create') && !server.status;
  const visible = links.filter((l) => l.show);
  if (visible.length === 0 && !canBackup) return null;
  return (
    <section aria-label="Quick actions">
      <SectionHeader title="Quick actions" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
        {canBackup && (
          <QuickButton
            icon={backup.isPending ? Loader2 : Archive}
            spin={backup.isPending}
            onClick={() => backup.mutate()}
            disabled={backup.isPending}
          >
            Back up now
          </QuickButton>
        )}
        {visible.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            params={{ server: id }}
            className="bg-card shadow-card hover:border-game/50 hover:bg-elevated/40 focus-visible:ring-ring/50 flex h-16 flex-col items-center justify-center gap-1.5 rounded-lg border text-sm font-medium transition-colors outline-none focus-visible:ring-[3px]"
          >
            <Icon className="text-game size-4" aria-hidden />
            {label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function QuickButton({
  icon: Icon,
  spin,
  children,
  ...rest
}: { icon: LucideIcon; spin?: boolean; children: ReactNode } & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      className="bg-card shadow-card hover:border-game/50 hover:bg-elevated/40 focus-visible:ring-ring/50 flex h-16 flex-col items-center justify-center gap-1.5 rounded-lg border text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] disabled:opacity-60"
      {...rest}
    >
      <Icon className={spin ? 'text-game size-4 animate-spin' : 'text-game size-4'} aria-hidden />
      {children}
    </button>
  );
}

function RecentActivity({ server }: { server: string }) {
  const query = useQuery(activityEventsQuery(server, '', 8));
  return (
    <section aria-label="Recent activity">
      <SectionHeader
        title="Recent activity"
        actions={
          <Button asChild size="sm" variant="ghost">
            <Link to="/server/$server/activity" params={{ server }}>
              All activity <ChevronRight />
            </Link>
          </Button>
        }
      />
      <QueryState query={query} skeleton={<Skeleton className="h-64 rounded-xl" />}>
        {(data) =>
          data.data.length === 0 ? (
            <EmptyState icon={History} title="No activity yet" className="py-10">
              Power actions, file changes and settings edits are recorded here.
            </EmptyState>
          ) : (
            <ul className="bg-card shadow-card divide-y rounded-xl border">
              {data.data.map((e) => (
                <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 truncate">{describeEvent(e.event)}</span>
                  {e.actor && (
                    <span className="text-muted-foreground hidden shrink-0 text-xs sm:inline">
                      {e.actor.username}
                    </span>
                  )}
                  <time
                    dateTime={e.createdAt}
                    className="text-muted-foreground shrink-0 text-xs tabular-nums"
                  >
                    {timeAgo(e.createdAt)}
                  </time>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
    </section>
  );
}
